import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Row,
  Spinner,
} from "reactstrap"
import { CurrentTapType, PaymentStatus } from "./ordersType"
import { OrderStatus } from "./Components/OrderStatus"
import "./style.scss"
import OrderItemCard from "./OrderComponents/OrderItemCard"
import CustomerInfo from "./OrderComponents/CustomerInfo"
import PaymentInfo from "./OrderComponents/PaymentInfo"
import DeliveryMan from "./OrderComponents/DeliveryMan"
import OrderState from "./OrderComponents/OrderState"
import { EditOrderModal } from "pages/vendor/Orders/Modals"
import { useTranslation } from "react-i18next"
import { getOrder } from "../../../store/orders/actions"
import { useNavigate } from "react-router-dom"

// Helper function to format date to '23 jun, 2022' format
const formatDate = (dateString: string): string => {
  if (!dateString) return "__"
  const date = new Date(dateString)
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
  const day = date.getDate()
  const month = months[date.getMonth()]
  const year = date.getFullYear()
  return `${day} ${month}, ${year}`
}

// Helper function to convert isPaid to PaymentStatus
const isPaidToPaymentStatus = (isPaid: boolean): PaymentStatus => {
  return isPaid ? PaymentStatus.paid : PaymentStatus.unpaid
}

const Order = () => {
  const { id } = useParams<{ id: string }>()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [copied, setCopied] = useState(false)
  const [showEditOrderModal, setShowEditOrderModal] = useState(false)

  // Get order from Redux store
  const { selectedOrder, loading } = useSelector((state: any) => state.Orders)

  // Fetch order on mount
  useEffect(() => {
    if (id) {
      dispatch(getOrder(Number(id), { push: navigate }))
    }
  }, [id, dispatch, navigate])

  // Map backend order data to the format expected by child components
  const order = React.useMemo(() => {
    if (!selectedOrder) {
      return null
    }

    return {
      id: `ID-${selectedOrder.id}`,
      createdDate: formatDate(selectedOrder.issueTime),
      orderStatus: selectedOrder.statusName,
      orderValue: `QAR ${selectedOrder.total}`,
      paymentDetails: {
        paymentStatus: isPaidToPaymentStatus(selectedOrder.isPaid),
        paymentMethod: selectedOrder.paymentMethod || "__",
        totalPrice: `QAR ${selectedOrder.subTotal}`,
        deliveryFee: `QAR ${selectedOrder.deliveryCost}`,
        discount: `QAR ${selectedOrder.discount}`,
        totalAmount: `QAR ${selectedOrder.total}`,
      },
      address: selectedOrder.address || "__",
      items: selectedOrder.itemsCount || "__",
      createdBy: {
        name: selectedOrder.customerName || "__",
        phone: selectedOrder.customerPhone || "__",
        image: selectedOrder.customerImage || "https://placehold.co/40x40",
      },
      // Use backend items data, mapping to the format expected by OrderItemCard
      itemsList: (selectedOrder.items || []).map((item: any) => ({
        name: item.productName || item.name || "__",
        price: `QAR ${item.price || item.unitPrice || 0}`,
        quantity: item.quantity || 1,
        category: item.categoryName || "__",
        brand: item.brand || "__",
        rate: item.rate || 5.0,
        image: item.image || item.productImage || "https://placehold.co/80x80",
      })),
      deliveryMan: selectedOrder.deliveryMan ? {
        name: selectedOrder.deliveryMan.name || "__",
        phone: selectedOrder.deliveryMan.phone || "__",
        image: selectedOrder.deliveryMan.image || "https://placehold.co/40x40",
      } : {
        name: "__",
        phone: "__",
        image: "https://placehold.co/40x40",
      },
      // Map backend eventLogs to orderState format for timeline display
      orderState: (selectedOrder.eventLogs || []).map((log: any) => ({
        date: formatDate(log.logTime),
        status: `${log.actionName} - ${log.byAccountName || "__"}${log.notes ? ` (${log.notes})` : ""}`,
      })).reverse(),
      coupon: selectedOrder.coupon || { total: 0, used: 0 },
    }
  }, [selectedOrder])

  const [openAccordion, setOpenAccordion] = useState({
    orderId: "0",
    basicInfo: "0",
    customerInfo: "0",
    paymentInfo: "0",
    deliverInfo: "0",
    orderState: "0",
  })

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500) // يختفي بعد 1.5 ثانية
    })
  }

  const percentage = React.useMemo(() => {
    if (!order || !order.coupon || order.coupon.total === 0) return 0
    return Math.min(
      ((order.coupon.total - order.coupon.used) / order.coupon.total) * 100,
      100
    )
  }, [order])

  // Show loading state
  if (loading) {
    return (
      <div className="page-content d-flex justify-content-center align-items-center">
        <Spinner color="primary" />
      </div>
    )
  }

  // Show error state if order not found
  if (!order) {
    return (
      <div className="page-content d-flex justify-content-center align-items-center">
        <p>{t("orders_orderNotFound")}</p>
      </div>
    )
  }

  return (
    <div className="page-content d-flex flex-column">
      <div className="container-fluid p-0">
        <div className="container-fluid d-flex justify-content-between align-items-center">
          <Breadcrumbs
            title={t("dashboard_dashboard")}
            breadcrumbItem={t("orders_orderDetrails")}
          />
          <button
            onClick={() => setShowEditOrderModal(true)}
            className="btn bg-gradiant-blue border-0 text-white"
          >
            <i className="bx bx-pencil"></i> {t("orders_editOrder")}
          </button>
        </div>
        {/* Order Data */}
        <Card className="text-center border border-1 shadow-sm bg-semi-white">
          <CardBody className="p-3 border-1 shadow">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex flex-column">
                <p className=" mb-0 text-muted">
                  <span className="font-size-16 text-semi-dark fw-semibold mx-2">
                    {t("orders_orderId")}
                  </span>
                  {order.id}
                  <button
                    className="relative cursor-pointer d-inline-block border-0 bg-transparent"
                    onClick={() => {
                      copyToClipboard(order.id)
                    }}
                  >
                    <i className="bx bx-copy px-2 text-primary bx-sm cursor-pointer"></i>
                    {copied && (
                      <span
                        className="position-absolute  translate-middle-x mt-1 text-success small"
                        style={{ top: "14px", marginLeft: "20px" }}
                      >
                        {t("common_copied")}
                      </span>
                    )}
                  </button>
                </p>
                <div className="text-muted fw-bold small mt-2 align-self-start">
                  <span className="mx-2 text-semi-dark text-size-14">
                    {t("common_createdAt")}
                  </span>
                  <span className="fw-normal">{order.createdDate}</span>
                </div>
              </div>
              <OrderStatus orderStatus={order.orderStatus} />
            </div>
          </CardBody>
        </Card>
        {/* Basic Info */}
        <Accordion
          open={openAccordion.basicInfo}
          className="border-0 bg-semi-white order-item mb-4 "
        >
          <AccordionItem>
            <AccordionHeader
              className="font-size-16 text-semi-dark fw-semibold mx-2"
              targetId="0"
              onClick={() =>
                setOpenAccordion({
                  ...openAccordion,
                  basicInfo: openAccordion.basicInfo === "0" ? "1" : "0",
                })
              }
            >
              {t("common_basicInfo")}
            </AccordionHeader>
            <AccordionBody accordionId="0">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <p className="mb-0 text-muted">
                  <span className="font-size-16 text-semi-dark fw-semibold mx-2">
                    {t("orders_totalPrice")}
                  </span>
                  {order.orderValue}
                </p>
                <p className="mb-0 text-muted">
                  <span className="font-size-16 text-semi-dark fw-semibold mx-2">
                    {t("orders_totalItems")}
                  </span>
                  {order.items}
                </p>
              </div>
              <Row>
                {order.itemsList.map((item: any, index: number) => (
                  <OrderItemCard key={index} item={item} />
                ))}
              </Row>

              {/* coupon */}
              {Object.keys(order.coupon).length > 0 && (
                <div className="">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <p className="mb-0 text-muted">
                      <span className="font-size-16 text-semi-dark fw-semibold mx-2">
                        {t("orders_couponBalance")}
                      </span>
                    </p>
                    <p className="mb-0 text-muted">
                      <span className="badge font-size-14 mx-2 bg-primary-30 text-primary">
                        {order.coupon.total - order.coupon.used}{" "}
                        {t("common_left")}
                      </span>
                    </p>
                  </div>
                  <div
                    className="progress bg-primary-20 rounded-full my-3"
                    style={{ height: "12px" }}
                  >
                    <div
                      className="progress-bar bg-primary"
                      role="progressbar"
                      style={{ width: `${percentage}%` }}
                    >
                      {order.coupon.used} / {order.coupon.total}
                    </div>
                  </div>
                </div>
              )}
              <div className="d-flex w-full justify-content-between">
                <span className="text-primary font-size-12">
                  {order.coupon.total} {t("orders_total")}
                </span>
                <span className="text-primary font-size-12">
                  {order.coupon.used} {t("orders_used")}
                </span>
                <span className="text-primary font-size-12">
                  {order.coupon.total - order.coupon.used}{" "}
                  {t("orders_remaining")}
                </span>
              </div>
            </AccordionBody>
          </AccordionItem>
        </Accordion>

        {/* Customer Info */}
        <CustomerInfo
          openAccordion={openAccordion}
          setOpenAccordion={setOpenAccordion}
          order={order}
        />

        {/* Payment Info */}
        <PaymentInfo
          openAccordion={openAccordion}
          setOpenAccordion={setOpenAccordion}
          order={order}
        />

        {/* Delivery Info */}
        <DeliveryMan
          openAccordion={openAccordion}
          setOpenAccordion={setOpenAccordion}
          order={order}
        />

        {/* Order State */}
        <OrderState
          openAccordion={openAccordion}
          setOpenAccordion={setOpenAccordion}
          order={order}
        />

        {/* Modals */}
        <EditOrderModal
          isOpen={showEditOrderModal}
          order={selectedOrder}
          toggle={() => setShowEditOrderModal(false)}
        />
      </div>
    </div>
  )
}

export default Order
